/* ---- Insert near top of file (helpers) ---- */
function _readCookie(name) {
  try {
    const nameEQ = name + '=';
    const ca = (document.cookie || '').split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
    }
  } catch (e) {}
  return null;
}

/* local fallback — toggles per-block .f-price spans */
function _localVatFallbackOnRoot(root) {
  try {
    if (!root) return;
    const cookie = _readCookie('vat_display');
    const INC = 'inc', EXC = 'exc';
    const mode = cookie || (() => {
      const f = root.querySelector('.f-price');
      if (!f) return EXC;
      const inc = f.querySelector('.f-price--inc');
      return (inc && window.getComputedStyle(inc).display !== 'none') ? INC : EXC;
    })();

    root.querySelectorAll('.f-price').forEach(wrap => {
      const incSpans = wrap.querySelectorAll('.f-price--inc');
      const excSpans = wrap.querySelectorAll('.f-price--exc');
      if (mode === INC) {
        incSpans.forEach(s => { s.style.display = ''; });
        excSpans.forEach(s => { s.style.display = 'none'; });
      } else {
        incSpans.forEach(s => { s.style.display = 'none'; });
        excSpans.forEach(s => { s.style.display = ''; });
      }
      const labelInc = wrap.querySelector('.vat-label--inc');
      const labelExc = wrap.querySelector('.vat-label--exc');
      if (labelInc && labelExc) {
        if (mode === INC) { labelInc.style.display = ''; labelExc.style.display = 'none'; }
        else { labelInc.style.display = 'none'; labelExc.style.display = ''; }
      }
      const unitInc = wrap.querySelector('[data-unit-price-inc]');
      const unitExc = wrap.querySelector('[data-unit-price-exc]');
      if (unitInc && unitExc) {
        if (mode === INC) { unitInc.style.display = ''; unitExc.style.display = 'none'; }
        else { unitInc.style.display = 'none'; unitExc.style.display = ''; }
      }
    });
  } catch (e) {
    // ignore
  }
}

/* helper to apply vat via available helpers + fallback + retries */
function _applyVatToRoot(root) {
  if (!root) return;
  try {
    // try window.vatHelpers first
    try {
      if (window.vatHelpers && typeof window.vatHelpers.applyVatModeToNode === 'function') {
        window.vatHelpers.applyVatModeToNode(root);
      }
    } catch (e) { /* ignore */ }

    // dispatch event for theme listeners that listen for vat:apply
    try { document.dispatchEvent(new CustomEvent('vat:apply', { detail: { root } })); } catch(e) {}

    // local fallback immediately
    _localVatFallbackOnRoot(root);

    // schedule retries (microtask + delayed) because some nodes may hydrate later
    Promise.resolve().then(() => {
      try { window.vatHelpers?.applyVatModeToNode?.(root); } catch(e) {}
    });
    setTimeout(() => {
      try { window.vatHelpers?.applyVatModeToNode?.(root); } catch(e) {}
      _localVatFallbackOnRoot(root);
    }, 120);
  } catch (err) {
    // ignore
  }
}

/* small observer to re-run if new nodes get added */
function _observeRootForVat(root) {
  if (!root) return;
  try {
    const mo = new MutationObserver((mutations) => {
      let need = false;
      for (const m of mutations) {
        if (m.addedNodes && m.addedNodes.length) {
          for (const n of m.addedNodes) {
            if (!(n instanceof Element)) continue;
            if (n.matches && (n.matches('.f-price') || n.matches('.product-card'))) { need = true; break; }
            if (n.querySelector && (n.querySelector('.f-price') || n.querySelector('.product-card'))) { need = true; break; }
          }
        }
        if (need) break;
      }
      if (need) _applyVatToRoot(root);
    });
    mo.observe(root, { childList: true, subtree: true });
    // return observer if you want to disconnect later
    return mo;
  } catch (e) { return null; }
}


class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // --- apply VAT to the injected content (like recommendations) ---
        try {
          // try to find a good root (prefer product grid/wrapper)
          const root = targetElement.querySelector('.products-grid-wrap') || targetElement;
          _applyVatToRoot(root);
          _observeRootForVat(root);
        } catch(e) {
          console.warn('VAT apply (predictive search) failed', e);
        }

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
